import { useState } from 'react';
import { Lock, Unlock, Edit2, Check } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Compact reusable inline toggle row showing:
 *   [FULL ▸ SNIPPET]  $unlock_price [✎]
 *
 * Props:
 *   value: { preview_mode, unlock_price }
 *   onChange: async ({ preview_mode, unlock_price }) => void  (updates backend)
 *   testid: prefix for data-testid
 */
export function PreviewModeToggle({ value, onChange, testid = 'preview' }) {
  const [editingPrice, setEditingPrice] = useState(false);
  const [priceInput, setPriceInput] = useState(String(value?.unlock_price ?? 0));
  const [saving, setSaving] = useState(false);
  const mode = value?.preview_mode === 'snippet' ? 'snippet' : 'full';
  const price = Number(value?.unlock_price) || 0;

  const toggleMode = async () => {
    const next = mode === 'snippet' ? 'full' : 'snippet';
    setSaving(true);
    try {
      await onChange({ preview_mode: next, unlock_price: price });
    } catch (e) {
      toast.error('Update failed');
    } finally {
      setSaving(false);
    }
  };

  const savePrice = async () => {
    const newPrice = Math.max(0, parseFloat(priceInput) || 0);
    setSaving(true);
    try {
      await onChange({ preview_mode: mode, unlock_price: newPrice });
      setEditingPrice(false);
    } catch (e) {
      toast.error('Update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="inline-flex items-center gap-2" data-testid={`${testid}-toggle-wrapper`}>
      <button
        type="button"
        onClick={toggleMode}
        disabled={saving}
        className={`text-[9px] font-bold uppercase tracking-wider px-2 py-1 border transition-colors flex items-center gap-1 ${mode === 'snippet' ? 'border-[#DAA520] bg-[#DAA520]/10 text-[#DAA520]' : 'border-[#22C55E]/40 bg-[#22C55E]/5 text-[#22C55E]'}`}
        title="Toggle full song vs 30-second snippet"
        data-testid={`${testid}-mode-btn`}
      >
        {mode === 'snippet' ? <Lock className="w-2.5 h-2.5" /> : <Unlock className="w-2.5 h-2.5" />}
        {mode === 'snippet' ? '30s SNIPPET' : 'FULL'}
      </button>
      {mode === 'snippet' && (
        editingPrice ? (
          <span className="inline-flex items-center gap-1">
            <span className="text-[9px] text-[#525252]">$</span>
            <input
              type="number"
              min="0"
              step="0.5"
              value={priceInput}
              autoFocus
              onChange={(e) => setPriceInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') savePrice(); if (e.key === 'Escape') setEditingPrice(false); }}
              onBlur={savePrice}
              className="w-14 bg-[#0A0A0A] border border-[#DAA520] outline-none text-[10px] font-mono px-1 py-0.5"
              data-testid={`${testid}-price-input`}
            />
            <button type="button" onClick={savePrice} className="text-[#22C55E] hover:text-white" data-testid={`${testid}-price-save`}>
              <Check className="w-2.5 h-2.5" />
            </button>
          </span>
        ) : (
          <button
            type="button"
            onClick={() => { setPriceInput(String(price)); setEditingPrice(true); }}
            className="text-[10px] font-mono text-[#A3A3A3] hover:text-[#DAA520] flex items-center gap-1 px-1.5 py-0.5 border border-[#262626] hover:border-[#DAA520]"
            title="Set unlock price (USD)"
            data-testid={`${testid}-price-btn`}
          >
            ${price.toFixed(2)} <Edit2 className="w-2.5 h-2.5" />
          </button>
        )
      )}
    </div>
  );
}
